import { useState, useMemo } from 'react'
import { useApi } from '@/hooks/useApi'
import type { BuzonHilo } from '@/lib/types'
import { Button } from '@/components/ui'
import { IconSend, IconX, IconAlertTriangle } from '@/components/icons/Icons'
import { nombreDe, emailDe } from '@/lib/correo-utils'

/*
  Responder un hilo del Buzón de Equipo.

  Dos cosas deliberadas, iguales a las de `ResponderCorreo` y por el mismo
  motivo:

  1. No hay campo para escribir una dirección. Los destinatarios son los del
     hilo y nada más; el backend rechaza cualquier otro con un 400 que lo
     nombra. Desde el ERP no se le puede escribir a una dirección arbitraria.
  2. Hay una confirmación antes de enviar. El correo sale con el nombre de una
     persona del equipo hacia un cliente y no se puede deshacer: no debería
     poder irse de un clic distraído.
*/
interface Props {
  hilo: BuzonHilo
  onEnviado: () => void
}

/** Escapa el texto del editor antes de armar el HTML del correo. */
function aHtml(texto: string): string {
  return texto
    .split('\n')
    .map((l) => l.replace(/[<>&]/g, (ch) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[ch]!)))
    .join('<br>')
}

export default function BuzonResponder({ hilo, onEnviado }: Props) {
  const api = useApi()
  const [abierto, setAbierto] = useState(false)
  const [cuerpo, setCuerpo] = useState('')
  const [para, setPara] = useState<string[]>([])
  const [confirmando, setConfirmando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Los candidatos son los participantes del hilo menos nuestra propia
  // dirección: contestarnos a nosotros mismos no es una opción útil.
  const candidatos = useMemo(() => {
    const propia = (hilo.remitente_email || '').toLowerCase()
    return (hilo.participantes || []).filter((p) => emailDe(p) !== propia)
  }, [hilo.participantes, hilo.remitente_email])

  // El id se fija al abrir el editor, no al enviar: así un segundo clic manda
  // el mismo id y el backend lo reconoce en vez de mandar el correo dos veces.
  const envioId = useMemo(
    () => `h${hilo.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    [hilo.id, abierto], // eslint-disable-line react-hooks/exhaustive-deps
  )

  function abrir() {
    setPara(candidatos)
    setCuerpo('')
    setError(null)
    setConfirmando(false)
    setAbierto(true)
  }

  async function enviar() {
    setEnviando(true)
    setError(null)
    try {
      await api.post(`/api/buzon/hilos/${hilo.id}/responder`, {
        envio_id: envioId,
        para,
        cuerpo_html: aHtml(cuerpo),
      })
      setAbierto(false)
      setCuerpo('')
      onEnviado()
    } catch (e: any) {
      setError(e?.message || 'No se pudo enviar la respuesta.')
      setConfirmando(false)
    } finally {
      setEnviando(false)
    }
  }

  if (candidatos.length === 0) {
    // Se dice por qué, en vez de esconder el botón sin explicación.
    return (
      <p className="text-muted" style={{ fontSize: 13, padding: '12px 14px' }}>
        Este hilo no tiene a quién responder: el único participante somos nosotros.
      </p>
    )
  }

  if (!abierto) {
    return (
      <div style={{ padding: '12px 14px' }}>
        <Button onClick={abrir}><IconSend size={14} /> Responder</Button>
      </div>
    )
  }

  return (
    <div className="card" style={{ margin: 14, padding: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <span className="text-muted" style={{ fontSize: 13 }}>Para:</span>
        {candidatos.map((d) => {
          const enPara = para.includes(d)
          return (
            <button
              key={d}
              className={`chip-destino ${enPara ? 'is-para' : 'is-fuera'}`}
              title={enPara ? 'Recibe la respuesta — clic para sacarlo' : 'No recibe esta respuesta — clic para agregarlo'}
              onClick={() => setPara(enPara ? para.filter((x) => x !== d) : [...para, d])}
            >
              {nombreDe(d)}
            </button>
          )
        })}
      </div>

      <textarea
        className="form-input"
        rows={7}
        placeholder="Escribí tu respuesta…"
        value={cuerpo}
        onChange={(e) => setCuerpo(e.target.value)}
        style={{ width: '100%', resize: 'vertical' }}
        autoFocus
      />

      <p className="text-muted" style={{ fontSize: 12.5, marginTop: 6 }}>
        Sale desde {hilo.remitente_nombre || hilo.remitente_email}, dentro de este hilo, con su firma.
      </p>

      {error && (
        <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 8 }}>
          <IconAlertTriangle size={13} /> {error}
        </p>
      )}

      {confirmando ? (
        <div style={{ marginTop: 12, padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
          <p style={{ fontSize: 14, marginBottom: 4 }}>
            <b>Se va a enviar a:</b> {para.join(', ') || '(nadie)'}
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 10 }}>
            El correo sale ahora y no se puede deshacer.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={enviar} disabled={enviando}>
              <IconSend size={14} /> {enviando ? 'Enviando…' : 'Confirmar y enviar'}
            </Button>
            <button className="btn btn-secondary" onClick={() => setConfirmando(false)} disabled={enviando}>
              Volver a editar
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button onClick={() => setConfirmando(true)} disabled={!cuerpo.trim() || para.length === 0}>
            <IconSend size={14} /> Revisar y enviar
          </Button>
          <button className="btn btn-secondary" onClick={() => setAbierto(false)}>
            <IconX size={14} /> Cancelar
          </button>
        </div>
      )}
    </div>
  )
}
